import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Alert,
  Box,
  Grid,
  Divider
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ImageUpload } from "../../ImageUpload";

const DisplayConfiguration = ({
  formData,
  onFormChange,
  jsonErrors,
  isComplete = false
}) => {
  const [displayConfig, setDisplayConfig] = useState({});
  const [localBannerUrl, setLocalBannerUrl] = useState("");
  const [localSearchKeyword, setLocalSearchKeyword] = useState("");
  const [localPrimaryColor, setLocalPrimaryColor] = useState("");

  // Parse the displayConfiguration JSON when component mounts or formData changes
  useEffect(() => {
    try {
      const parsed = JSON.parse(formData.displayConfiguration || "{}");
      setDisplayConfig(parsed);
      setLocalBannerUrl(parsed.bannerUrl || "");
      setLocalSearchKeyword(parsed.searchKeyword || "");
      setLocalPrimaryColor(parsed.primaryColor || "");
    } catch (error) {
      setDisplayConfig({});
      setLocalBannerUrl("");
      setLocalSearchKeyword("");
      setLocalPrimaryColor("");
    }
  }, [formData.displayConfiguration]);

  // Update the JSON when widget values change
  const updateDisplayConfig = (updates) => {
    const newConfig = { ...displayConfig, ...updates };
    setDisplayConfig(newConfig);
    try {
      const jsonString = JSON.stringify(newConfig, null, 2);
      onFormChange("displayConfiguration", jsonString);
    } catch (error) {
      // Handle JSON parsing errors
    }
  };

  const handleBannerUrlChange = (url) => {
    setLocalBannerUrl(url);
    updateDisplayConfig({ bannerUrl: url });
  };

  const handleSearchKeywordChange = (keyword) => {
    setLocalSearchKeyword(keyword);
    updateDisplayConfig({ searchKeyword: keyword });
  };

  const handlePrimaryColorChange = (color) => {
    setLocalPrimaryColor(color);
    updateDisplayConfig({ primaryColor: color });
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1">Display Configuration</Typography>
          {isComplete && <CheckCircleIcon color="success" />}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Configure display settings including banner images, detail display, carousels, and collective impact sections.
        </Typography>

        {/* Widgets Section */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <Grid container spacing={3}>
            {/* Banner Image Upload */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Banner Image
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload a banner image for the spending goal
                </Typography>
                <ImageUpload
                  image={localBannerUrl}
                  onChange={({ target }) => handleBannerUrlChange(target.value)}
                  name="bannerUrl"
                  imageAlt="spending goal banner"
                  ratio="16:9"
                  sx={{ width: "100%", height: "200px" }}
                />
              </Box>
            </Grid>

            {/* Search Keyword Input */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Search Keyword
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Enter a keyword to search for related content.
                </Typography>
                <TextField
                  fullWidth
                  label="Search Keyword"
                  margin="normal"
                  value={localSearchKeyword}
                  onChange={(e) => handleSearchKeywordChange(e.target.value)}
                  placeholder="Enter search keyword..."
                  helperText="Keyword for searching related content"
                />
              </Box>
            </Grid>

            {/* Primary Color Input */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Primary Color
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Choose the primary color for the spending goal theme
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <input
                    type="color"
                    value={localPrimaryColor || "#000000"}
                    onChange={(e) => handlePrimaryColorChange(e.target.value)}
                    style={{
                      width: "60px",
                      height: "40px",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer"
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Hex Color"
                    value={localPrimaryColor}
                    onChange={(e) => handlePrimaryColorChange(e.target.value)}
                    placeholder="#000000"
                    helperText="Enter hex color code (e.g., #FF5733)"
                    inputProps={{
                      pattern: "^#[0-9A-Fa-f]{6}$",
                      maxLength: 7
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* JSON Editor Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Advanced Configuration (JSON)
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Edit the raw JSON configuration for advanced display settings, carousels, etc.
          </Typography>
          <TextField
            fullWidth
            label="Display Configuration (JSON)"
            margin="normal"
            multiline
            rows={8}
            value={formData.displayConfiguration}
            onChange={(e) => onFormChange("displayConfiguration", e.target.value)}
            error={jsonErrors.displayConfiguration}
            helperText={
              jsonErrors.displayConfiguration
                ? "Invalid JSON format"
                : "Configuration for display settings, carousels, etc."
            }
          />
          {jsonErrors.displayConfiguration && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Please enter valid JSON for display configuration
            </Alert>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default DisplayConfiguration;
